/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/

import { LIBRARY_GET_RECEIVE } from './actions';
import { Gender, LibraryData, LogoType, StraussLogoType } from './interfaces';


export const initialState :  LibraryData = {
    "baseColors": [
      {
        "id": "01",
        "color": "#000000",
        "bgColor": "#555555",
        "name": {
          "de": "Schwarz",
          "en": "black",
          "fr": "noir"
        }
      },
      {
        "id": "02",
        "color": "#32353B",
        "bgColor": "#5d6169",
        "name": {
          "de": "Graphit",
          "en": "graphite",
          "fr": "graphite"
        }
      },
      {
        "id": "03",
        "color": "#4F5556",
        "bgColor": "#686e6f",
        "name": {
          "de": "Anthrazit",
          "en": "anthracite",
          "fr": "anthracite"
        }
      },
      {
        "id": "04",
        "color": "#0E1F40",
        "bgColor": "#284274",
        "name": {
          "de": "Dunkelblau",
          "en": "navy",
          "fr": "bleu foncé"
        }
      },
      {
        "id": "05",
        "color": "#4E403D",
        "bgColor": "#665653",
        "name": {
          "de": "Kastanie",
          "en": "chestnut",
          "fr": "marron"
        }
      },
      {
        "id": "06",
        "color": "#ffffff",
        "modelColor": "#cccccc",
        "bgColor": "#ecebe8",
        "name": {
          "de": "Weiss",
          "en": "white",
          "fr": "blanc"
        }
      },
      {
        "id": "07",
        "color": "#003F0A",
        "bgColor": "#319441",
        "name": {
          "de": "Grün",
          "en": "green",
          "fr": "vert"
        }
      },
      {
        "id": "08",
        "color": "#0031AF",
        "bgColor": "#2d55bd",
        "modelColor": "#1e4989",
        "name": {
          "de": "Kornblau",
          "en": "royalblue",
          "fr": "bleu royal"
        }
      },
      {      
        "id":"10",
        "color":"#ada49b",
        "bgColor":"#ada49b",
        "name":{
          "de":"Lehm",
          "en":"clay",
          "fr":"glaise"
        }
      }
    ],
    "contrastColors": [
      {
        "id": "14",
        "color": "#FFED00",
        "bgColor": "#FFED00",
        "name": {
          "de": "Warngelb",
          "en": "high-vis-yellow",
          "fr": "jaune"
        },
        "searchString": {
          "de": "Gelb",
          "en": "yellow",
          "fr": "jaune"
        }
      },
      {
        "id": "15",
        "color": "#F39200",
        "bgColor": "#F39200",
        "name": {
          "de": "Warnorange",
          "en": "high-vis-orange",
          "fr": "orange"
        },
        "searchString": {
          "de": "Orange",
          "en": "orange",
          "fr": "orange"
        }
      },
      {
        "id": "09",
        "color": "#918A91",
        "bgColor": "#918A91",
        "name": {
          "de": "Platin",
          "en": "platinum",
          "fr": "platine"
        }
      },
      {
        "id": "16",
        "color": "#1C5CA3",
        "bgColor": "#1C5CA3",
        "name": {
          "de": "Enzianblau",
          "en": "gentian blue",
          "fr": "bleu gentiane"
        }
      },
      {
        "id": "12",
        "color": "#0E6A8F",
        "bgColor": "#0E6A8F",
        "name": {
          "de": "Atoll",
          "en": "atoll",
          "fr": "atoll"
        }
      },
      {
        "id": "17",
        "color": "#789D3C",
        "bgColor": "#789D3C",
        "name": {
          "de": "Seegrün",
          "en": "sea green",
          "fr": "vert d'eau"
        }
      },
      {
        "id": "11",
        "color": "#B40F36",
        "modelColor": "#a11d32",
        "bgColor": "#B40F36",
        "name": {
          "de": "Feuerrot",
          "en": "fire red",
          "fr": "rouge vif"
        }
      },
      {
        "id": "18",
        "color": "#194951",
        "bgColor": "#194951",
        "name": {
          "de": "Seeblau",
          "en": "sea blue",
          "fr": "bleu marin"
        }
      }
    ],
    "products": [
      {
        "article": "10",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_west_de.html",
          "en": "./descriptions/softshell_west_en.html",
          "fr": "./descriptions/softshell_west_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Softshell Weste e.s.mymotion 2020, Damen CI",
          "en": "Softshell bodywarmer e.s.mymotion 2020, ladies' CI",
          "fr": "Gilet softshell e.s.mymotion 2020, femmes CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 49.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 46.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 44.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS",
          "S",
          "M",
          "L",
          "XL",
          "2XL"
        ],
        "tags": [
          "mymymotion2020",
          "female",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":2,
            "name":{
              "de":"Left chest pocket",
              "en":"Left chest pocket",
              "fr":"Left chest pocket"
            },
            "transferSize": {
              "width": 6,
              "height": 6
            }
          },
          {
            "id":35,
            "name":{
              "de":"Left shoulder blade",
              "en":"Left shoulder blade",
              "fr":"Left shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":36,
            "name":{
              "de":"Back left shoulder seam",
              "en":"Back left shoulder seam",
              "fr":"Back left shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":39,
            "name":{
              "de":"Right shoulder blade",
              "en":"Right shoulder blade",
              "fr":"Right shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":40,
            "name":{
              "de":"Back right shoulder seam",
              "en":"Back right shoulder seam",
              "fr":"Back right shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 22,
              "height": 10
            },
            "transferSize": {
              "width": 25,
              "height": 25
            }
          },
          {
            "id":44,
            "name":{
              "de":"Middle of the neck",
              "en":"Middle of the neck",
              "fr":"Middle of the neck"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":45,
            "name":{
              "de":"Back above the seam/hem middle",
              "en":"Back above the seam/hem middle",
              "fr":"Back above the seam/hem middle"
            },
            "stitchSize": {
              "width": 15,
              "height": 3
            },
            "transferSize": {
              "width": 15,
              "height": 5
            }
          },
          {
            "id":73,
            "name":{
              "de":"Right vertical parallel to the zip",
              "en":"Right vertical parallel to the zip",
              "fr":"Right vertical parallel to the zip"
            },
            "stitchSize": {
              "width": 3,
              "height": 20
            },
            "transferSize": {
              "width": 5,
              "height": 20
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          }
        ]
      },
      {
        "article": "11",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_west_de.html",
          "en": "./descriptions/softshell_west_en.html",
          "fr": "./descriptions/softshell_west_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Softshell Weste e.s.mymotion 2020 CI",
          "en": "Softshell bodywarmer e.s.mymotion 2020 CI",
          "fr": "Gilet softshell e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 49.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 46.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 44.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS", 
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL",
          "3XL", 
          "4XL", 
          "5XL"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":2,
            "name":{
              "de":"Left chest pocket",
              "en":"Left chest pocket",
              "fr":"Left chest pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":7,
            "name":{
              "de":"Front left shoulder seam",
              "en":"Front left shoulder seam",
              "fr":"Front left shoulder seam"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":22,
            "name":{
              "de":"Front right shoulder seam",
              "en":"Front right shoulder seam",
              "fr":"Front right shoulder seam"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":35,
            "name":{
              "de":"Left shoulder blade",
              "en":"Left shoulder blade",
              "fr":"Left shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":36,
            "name":{
              "de":"Back left shoulder seam",
              "en":"Back left shoulder seam",
              "fr":"Back left shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":39,
            "name":{
              "de":"Right shoulder blade",
              "en":"Right shoulder blade",
              "fr":"Right shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":40,
            "name":{
              "de":"Back right shoulder seam",
              "en":"Back right shoulder seam",
              "fr":"Back right shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 25,
              "height": 15
            },
            "transferSize": {
              "width": 30,
              "height": 30
            }
          },
          {
            "id":44,
            "name":{
              "de":"Middle of the neck",
              "en":"Middle of the neck",
              "fr":"Middle of the neck"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":45,
            "name":{
              "de":"Back above the seam/hem middle",
              "en":"Back above the seam/hem middle",
              "fr":"Back above the seam/hem middle"
            },
            "stitchSize": {
              "width": 20,
              "height": 3
            },
            "transferSize": {
              "width": 20,
              "height": 5
            }
          },
          {
            "id":73,
            "name":{
              "de":"Right vertical parallel to the zip",
              "en":"Right vertical parallel to the zip",
              "fr":"Right vertical parallel to the zip"
            },
            "stitchSize": {
              "width": 3,
              "height": 20
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 30
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 30
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          }
        ]
      },
      {
        "article": "12",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_west_winter_f_de.html",
          "en": "./descriptions/softshell_west_winter_f_en.html",
          "fr": "./descriptions/softshell_west_winter_f_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Winter Softshellweste e.s.mymotion 2020, Damen CI",
          "en": "Winter softshellbodywarmer e.s.mymotion 2020, ladies' CI",
          "fr": "D'hiver giletsoftshell e.s.mymotion 2020, femmes CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 92.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 87.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 82.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 77.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 72.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS", 
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 20,
              "height": 10
            },
            "transferSize": {
              "width": 25,
              "height": 25
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":113,
            "name":{
              "de":"Left chest side below seam",
              "en":"Left chest side below seam",
              "fr":"Left chest side below seam"
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          }
        ]
      },
      {
        "article": "13",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_west_winter_de.html",
          "en": "./descriptions/softshell_west_winter_en.html",
          "fr": "./descriptions/softshell_west_winter_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Winter Softshellweste e.s.mymotion 2020 CI",
          "en": "Winter softshell bodywarmer e.s.mymotion 2020 CI",
          "fr": "D'hiver gilet softshell e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 92.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 87.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 82.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 77.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 72.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL",
          "3XL", 
          "4XL"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":2,
            "name":{
              "de":"Left chest pocket",
              "en":"Left chest pocket",
              "fr":"Left chest pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":7,
            "name":{
              "de":"Front left shoulder seam",
              "en":"Front left shoulder seam",
              "fr":"Front left shoulder seam"
            },
            "transferSize": {
              "width": 6,
              "height": 2
            }
          },
          {
            "id":22,
            "name":{
              "de":"Front right shoulder seam",
              "en":"Front right shoulder seam",
              "fr":"Front right shoulder seam"
            },
            "transferSize": {
              "width": 6,
              "height": 2
            }
          },
          {
            "id":35,
            "name":{
              "de":"Left shoulder blade",
              "en":"Left shoulder blade",
              "fr":"Left shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":36,
            "name":{
              "de":"Back left shoulder seam",
              "en":"Back left shoulder seam",
              "fr":"Back left shoulder seam"
            },
            "stitchSize": {
              "width": 8,
              "height": 3
            },
            "transferSize": {
              "width": 8,
              "height": 3
            }
          },
          {
            "id":39,
            "name":{
              "de":"Right shoulder blade",
              "en":"Right shoulder blade",
              "fr":"Right shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":40,
            "name":{
              "de":"Back right shoulder seam",
              "en":"Back right shoulder seam",
              "fr":"Back right shoulder seam"
            },
            "stitchSize": {
              "width": 8,
              "height": 3
            },
            "transferSize": {
              "width": 8,
              "height": 3
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 25,
              "height": 15
            },
            "transferSize": {
              "width": 30,
              "height": 30
            }
          },
          {
            "id":44,
            "name":{
              "de":"Middle of the neck",
              "en":"Middle of the neck",
              "fr":"Middle of the neck"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":45,
            "name":{
              "de":"Back above the seam/hem middle",
              "en":"Back above the seam/hem middle",
              "fr":"Back above the seam/hem middle"
            },
            "stitchSize": {
              "width": 22,
              "height": 3
            },
            "transferSize": {
              "width": 30,
              "height": 10
            }
          },
          {
            "id":73,
            "name":{
              "de":"Right vertical parallel to the zip",
              "en":"Right vertical parallel to the zip",
              "fr":"Right vertical parallel to the zip"
            },
            "stitchSize": {
              "width": 3,
              "height": 15
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 30
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 30
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          }
        ]
      },
      {
        "article": "14",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_de.html",
          "en": "./descriptions/softshell_en.html",
          "fr": "./descriptions/softshell_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Softshelljacke e.s.mymotion 2020, Damen CI",
          "en": "Softshell jacket e.s.mymotion 2020, ladies CI",
          "fr": "Veste Softshell e.s.mymotion 2020, CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 65.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 61.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 58.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS",
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL",
          "3XL"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":2,
            "name":{
              "de":"Left chest pocket",
              "en":"Left chest pocket",
              "fr":"Left chest pocket"
            },
            "transferSize": {
              "width": 6,
              "height": 6
            }
          },
          {
            "id":26,
            "name":{
              "de":"Right upper arm",
              "en":"Right upper arm",
              "fr":"Right upper arm"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 3
            }
          },
          {
            "id":35,
            "name":{
              "de":"Left shoulder blade",
              "en":"Left shoulder blade",
              "fr":"Left shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":36,
            "name":{
              "de":"Back left shoulder seam",
              "en":"Back left shoulder seam",
              "fr":"Back left shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":39,
            "name":{
              "de":"Right shoulder blade",
              "en":"Right shoulder blade",
              "fr":"Right shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":40,
            "name":{
              "de":"Back right shoulder seam",
              "en":"Back right shoulder seam",
              "fr":"Back right shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 22,
              "height": 10
            },
            "transferSize": {
              "width": 25,
              "height": 25
            }
          },
          {
            "id":44,
            "name":{
              "de":"Middle of the neck",
              "en":"Middle of the neck",
              "fr":"Middle of the neck"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":45,
            "name":{
              "de":"Back above the seam/hem middle",
              "en":"Back above the seam/hem middle",
              "fr":"Back above the seam/hem middle"
            },
            "stitchSize": {
              "width": 15,
              "height": 3
            },
            "transferSize": {
              "width": 15,
              "height": 5
            }
          },
          {
            "id":73,
            "name":{
              "de":"Right vertical parallel to the zip",
              "en":"Right vertical parallel to the zip",
              "fr":"Right vertical parallel to the zip"
            },
            "stitchSize": {
              "width": 3,
              "height": 20
            },
            "transferSize": {
              "width": 5,
              "height": 20
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          }
        ]
      },
      {
        "article": "15",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_de.html",
          "en": "./descriptions/softshell_en.html",
          "fr": "./descriptions/softshell_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Softshelljacke e.s.mymotion 2020 CI",
          "en": "Softshell jacket e.s.mymotion 2020 CI",
          "fr": "Veste Softshell e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 65.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 61.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 58.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS", 
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL",
          "3XL", 
          "4XL", 
          "5XL", 
          "6XL"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":2,
            "name":{
              "de":"Left chest pocket",
              "en":"Left chest pocket",
              "fr":"Left chest pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":7,
            "name":{
              "de":"Front left shoulder seam",
              "en":"Front left shoulder seam",
              "fr":"Front left shoulder seam"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":10,
            "name":{
              "de":"Above the seam/hem left",
              "en":"Above the seam/hem left",
              "fr":"Above the seam/hem left"
            },
            "stitchSize": {
              "width": 10,
              "height": 5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":22,
            "name":{
              "de":"Front right shoulder seam",
              "en":"Front right shoulder seam",
              "fr":"Front right shoulder seam"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":26,
            "name":{
              "de":"Right upper arm",
              "en":"Right upper arm",
              "fr":"Right upper arm"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 3
            }
          },
          {
            "id":35,
            "name":{
              "de":"Left shoulder blade",
              "en":"Left shoulder blade",
              "fr":"Left shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":36,
            "name":{
              "de":"Back left shoulder seam",
              "en":"Back left shoulder seam",
              "fr":"Back left shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":39,
            "name":{
              "de":"Right shoulder blade",
              "en":"Right shoulder blade",
              "fr":"Right shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":40,
            "name":{
              "de":"Back right shoulder seam",
              "en":"Back right shoulder seam",
              "fr":"Back right shoulder seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 26,
              "height": 15
            },
            "transferSize": {
              "width": 30,
              "height": 30
            }
          },
          {
            "id":44,
            "name":{
              "de":"Middle of the neck",
              "en":"Middle of the neck",
              "fr":"Middle of the neck"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":45,
            "name":{
              "de":"Back above the seam/hem middle",
              "en":"Back above the seam/hem middle",
              "fr":"Back above the seam/hem middle"
            },
            "stitchSize": {
              "width": 20,
              "height": 3
            },
            "transferSize": {
              "width": 30,
              "height": 5
            }
          },
          {
            "id":73,
            "name":{
              "de":"Right vertical parallel to the zip",
              "en":"Right vertical parallel to the zip",
              "fr":"Right vertical parallel to the zip"
            },
            "stitchSize": {
              "width": 3,
              "height": 20
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 30
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 30
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":91,
            "name":{
              "de":"Right upper arm vertical",
              "en":"Right upper arm vertical",
              "fr":"Right upper arm vertical"
            },
            "transferSize": {
              "width": 3,
              "height": 15
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          }
        ]
      },
      {
        "article": "16",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_winter_f_de.html",
          "en": "./descriptions/softshell_winter_f_en.html",
          "fr": "./descriptions/softshell_winter_f_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Winter Softshelljacke e.s.mymotion 2020, Damen CI",
          "en": "Winter softshell jacket e.s.mymotion 2020, ladies CI",
          "fr": "D'hiver veste softshell e.s.mymotion 2020, femmes CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 119.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 114.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 109.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 104.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 99.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS", 
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL",
          "3XL"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":26,
            "name":{
              "de":"Right upper arm",
              "en":"Right upper arm",
              "fr":"Right upper arm"
            },
            "stitchSize": {
              "width": 8,
              "height": 5
            },
            "transferSize": {
              "width": 8,
              "height": 5
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 20,
              "height": 10
            },
            "transferSize": {
              "width": 25,
              "height": 25
            }
          },
          {
            "id":84,
            "name":{
              "de":"Back side left",
              "en":"Back side left",
              "fr":"Back side left"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":85,
            "name":{
              "de":"Back side right",
              "en":"Back side right",
              "fr":"Back side right"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":113,
            "name":{
              "de":"Left chest side below seam",
              "en":"Left chest side below seam",
              "fr":"Left chest side below seam"
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
        ]
      },
      {
        "article": "17",
        "articleDescriptionUrl": {
          "de": "./descriptions/softshell_winter_de.html",
          "en": "./descriptions/softshell_winter_en.html",
          "fr": "./descriptions/softshell_winter_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Winter Softshelljacke e.s.mymotion 2020, Herren CI",
          "en": "Winter softshell jacket e.s.mymotion 2020, men's CI",
          "fr": "D'hiver veste softshell e.s.mymotion 2020, hommes CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 119.90,
              "chf": 0
            }
          },
          {
            "minAmount": 50,
            "pricing": {
              "eur": 114.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 109.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 104.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 99.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "XS", 
          "S", 
          "M", 
          "L", 
          "XL", 
          "2XL",
          "3XL",
          "4XL",
          "5XL"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "jackets"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, 0.0, 2.0],
          "cameraStartPositionRight": [1.0, 0.0, 2.0],
          "cameraTargetPosition": [0.0, 0.0, 0.0]
        },
        "logoPlanes":[
          {
            "id":2,
            "name":{
              "de":"Left chest pocket",
              "en":"Left chest pocket",
              "fr":"Left chest pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":26,
            "name":{
              "de":"Right upper arm",
              "en":"Right upper arm",
              "fr":"Right upper arm"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 3
            }
          },
          {
            "id":35,
            "name":{
              "de":"Left shoulder blade",
              "en":"Left shoulder blade",
              "fr":"Left shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":39,
            "name":{
              "de":"Right shoulder blade",
              "en":"Right shoulder blade",
              "fr":"Right shoulder blade"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":43,
            "name":{
              "de":"Middle of the back",
              "en":"Middle of the back",
              "fr":"Middle of the back"
            },
            "stitchSize": {
              "width": 30,
              "height": 10
            },
            "transferSize": {
              "width": 30,
              "height": 30
            }
          },
          {
            "id":45,
            "name":{
              "de":"Back above the seam/hem middle",
              "en":"Back above the seam/hem middle",
              "fr":"Back above the seam/hem middle"
            },
            "transferSize": {
              "width": 20,
              "height": 5
            }
          },
          {
            "id":73,
            "name":{
              "de":"Right vertical parallel to the zip",
              "en":"Right vertical parallel to the zip",
              "fr":"Right vertical parallel to the zip"
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":88,
            "name":{
              "de":"Left chest side above seam",
              "en":"Left chest side above seam",
              "fr":"Left chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":91,
            "name":{
              "de":"Right upper arm vertical",
              "en":"Right upper arm vertical",
              "fr":"Right upper arm vertical"
            },
            "transferSize": {
              "width": 3,
              "height": 15
            }
          },
          {
            "id":95,
            "name":{
              "de":"Right chest side below seam",
              "en":"Right chest side below seam",
              "fr":"Right chest side below seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":96,
            "name":{
              "de":"Right chest side above seam",
              "en":"Right chest side above seam",
              "fr":"Right chest side above seam"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          }
        ]
      },
      {
        "article": "18",
        "articleDescriptionUrl": {
          "de": "./descriptions/trousers_de.html",
          "en": "./descriptions/trousers_en.html",
          "fr": "./descriptions/trousers_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "ventilationArticle": "19",
        "name": {
          "de": "Damenhose e.s.mymotion 2020 CI",
          "en": "Ladies' trousers e.s.mymotion 2020 CI",
          "fr": "Pantalon pour femmes e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 79.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 74.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "32", 
          "34",
          "36",
          "38",
          "40",
          "42",
          "44",
          "46",
          "48",
          "34S",
          "36S",
          "38S",
          "40S",
          "42S",
          "44S",
          "46S",
          "34L",
          "36L",
          "38L",
          "40L",
          "42L",
          "44L",
          "46L"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.5, 2.0],
          "cameraStartPositionRight": [1.0, -0.5, 2.0],
          "cameraTargetPosition": [0.0, -0.5, 0.0]
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          }
        ]
      },
      {
        "article": "19",
        "articleDescriptionUrl": {
          "de": "./descriptions/trousers_vent_de.html",
          "en": "./descriptions/trousers_vent_en.html",
          "fr": "./descriptions/trousers_vent_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": true,
        "nonVentilationArticle": "18",
        "name": {
          "de": "Damenhose e.s.mymotion 2020 CI",
          "en": "Ladies' trousers e.s.mymotion 2020 CI",
          "fr": "Pantalon pour femmes e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 74.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "32", 
          "34",
          "36",
          "38",
          "40",
          "42",
          "44",
          "46",
          "48",
          "34S",
          "36S",
          "38S",
          "40S",
          "42S",
          "44S",
          "46S",
          "34L",
          "36L",
          "38L",
          "40L",
          "42L",
          "44L",
          "46L"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.5, 2.0],
          "cameraStartPositionRight": [1.0, -0.5, 2.0],
          "cameraTargetPosition": [0.0, -0.5, 0.0]
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          }
        ]
      },
      {
        "article": "20",
        "articleDescriptionUrl": {
          "de": "./descriptions/trousers_de.html",
          "en": "./descriptions/trousers_en.html",
          "fr": "./descriptions/trousers_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "ventilationArticle": "21",
        "name": {
          "de": "Bundhose e.s.mymotion 2020 CI",
          "en": "Trousers e.s.mymotion 2020 CI",
          "fr": "Pantalon à taille élastique e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 79.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 74.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "38",
          "40",
          "42",
          "44",
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58",
          "60",
          "62",
          "64",
          "66",
          "68",
          "23",
          "24",
          "25",
          "26",
          "27",
          "28",
          "29",
          "30",
          "86",
          "90",
          "94",
          "98",
          "102",
          "106",
          "110",
          "114"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.5, 2.0],
          "cameraStartPositionRight": [1.0, -0.5, 2.0],
          "cameraTargetPosition": [0.0, -0.5, 0.0]
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 3.5
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":50,
            "name":{
              "de":"Below the left trouser pocket",
              "en":"Below the left trouser pocket",
              "fr":"Below the left trouser pocket"
            },
            "transferSize": {
              "width": 8,
              "height": 5
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          }
        ]
      },
      {
        "article": "21",
        "articleDescriptionUrl": {
          "de": "./descriptions/trousers_vent_de.html",
          "en": "./descriptions/trousers_vent_en.html",
          "fr": "./descriptions/trousers_vent_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": true,
        "nonVentilationArticle": "20",
        "name": {
          "de": "Bundhose e.s.mymotion 2020 CI",
          "en": "Trousers e.s.mymotion 2020 CI",
          "fr": "Pantalon à taille élastique e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 74.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "38",
          "40",
          "42",
          "44",
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58",
          "60",
          "62",
          "64",
          "66",
          "68",
          "23",
          "24",
          "25",
          "26",
          "27",
          "28",
          "29",
          "30",
          "86",
          "90",
          "94",
          "98",
          "102",
          "106",
          "110",
          "114"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.5, 2.0],
          "cameraStartPositionRight": [1.0, -0.5, 2.0],
          "cameraTargetPosition": [0.0, -0.5, 0.0]
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 3.5
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":50,
            "name":{
              "de":"Below the left trouser pocket",
              "en":"Below the left trouser pocket",
              "fr":"Below the left trouser pocket"
            },
            "transferSize": {
              "width": 8,
              "height": 5
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          }
        ]
      },
      {
        "article": "22",
        "articleDescriptionUrl": {
          "de": "./descriptions/bib_f_de.html",
          "en": "./descriptions/bib_f_en.html",
          "fr": "./descriptions/bib_f_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "ventilationArticle": "23",
        "name": {
          "de": "Damen Latzhose e.s.mymotion 2020 CI",
          "en": "Ladies' bib & brace e.s.mymotion 2020 CI",
          "fr": "Femmes salopette e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 88.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 84.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 77.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 74.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "32", 
          "34",
          "36",
          "38",
          "40",
          "42",
          "44",
          "46"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.35, 2.0],
          "cameraStartPositionRight": [1.0, -0.35, 2.0],
          "cameraTargetPosition": [0.0, -0.35, 0.0],
          "minDistance": 1.6,
          "maxDistance": 1.9
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":74,
            "name":{
              "de":"Bib pocket",
              "en":"Bib pocket",
              "fr":"Bib pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":77,
            "name":{
              "de":"Bib&brace brace connector",
              "en":"Bib&brace brace connector",
              "fr":"Bib&brace brace connector"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          }
        ]
      },
      {
        "article": "23",
        "articleDescriptionUrl": {
          "de": "./descriptions/bib_f_vent_de.html",
          "en": "./descriptions/bib_f_vent_en.html",
          "fr": "./descriptions/bib_f_vent_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": true,
        "nonVentilationArticle": "22",
        "name": {
          "de": "Damen Latzhose e.s.mymotion 2020 CI",
          "en": "Ladies' bib & brace e.s.mymotion 2020 CI",
          "fr": "Femmes salopette e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 83.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 79.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 72.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "32", 
          "34",
          "36",
          "38",
          "40",
          "42",
          "44",
          "46"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.35, 2.0],
          "cameraStartPositionRight": [1.0, -0.35, 2.0],
          "cameraTargetPosition": [0.0, -0.35, 0.0],
          "minDistance": 1.6,
          "maxDistance": 1.9
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":74,
            "name":{
              "de":"Bib pocket",
              "en":"Bib pocket",
              "fr":"Bib pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":77,
            "name":{
              "de":"Bib&brace brace connector",
              "en":"Bib&brace brace connector",
              "fr":"Bib&brace brace connector"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 20
            }
          }
        ]
      },
      {
        "article": "24",
        "articleDescriptionUrl": {
          "de": "./descriptions/bib_de.html",
          "en": "./descriptions/bib_en.html",
          "fr": "./descriptions/bib_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "ventilationArticle": "25",
        "name": {
          "de": "Latzhose e.s.mymotion 2020 CI",
          "en": "Bib & brace e.s.mymotion 2020 CI",
          "fr": "Salopette e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 88.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 84.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 77.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 74.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "44",
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58",
          "60",
          "62",
          "64",
          "66",
          "68",
          "23",
          "24",
          "25",
          "26",
          "27",
          "28",
          "29",
          "30",
          "86",
          "90",
          "94",
          "98",
          "102",
          "106"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.4, 2.0],
          "cameraStartPositionRight": [1.0, -0.4, 2.0],
          "cameraTargetPosition": [0.0, -0.4, 0.0],
          "minDistance": 1.6,
          "maxDistance": 1.9
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":74,
            "name":{
              "de":"Bib pocket",
              "en":"Bib pocket",
              "fr":"Bib pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":75,
            "name":{
              "de":"Above the bib pocket",
              "en":"Above the bib pocket",
              "fr":"Above the bib pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":77,
            "name":{
              "de":"Bib&brace brace connector",
              "en":"Bib&brace brace connector",
              "fr":"Bib&brace brace connector"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          }
        ]
      },
      {
        "article": "25",
        "articleDescriptionUrl": {
          "de": "./descriptions/bib_vent_de.html",
          "en": "./descriptions/bib_vent_en.html",
          "fr": "./descriptions/bib_vent_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": true,
        "nonVentilationArticle": "24",
        "name": {
          "de": "Latzhose e.s.mymotion 2020 CI",
          "en": "Bib & brace e.s.mymotion 2020 CI",
          "fr": "Salopette e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 83.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 79.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 72.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "44",
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58",
          "60",
          "62",
          "64",
          "66",
          "68",
          "23",
          "24",
          "25",
          "26",
          "27",
          "28",
          "29",
          "30",
          "86",
          "90",
          "94",
          "98",
          "102",
          "106"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.4, 2.0],
          "cameraStartPositionRight": [1.0, -0.4, 2.0],
          "cameraTargetPosition": [0.0, -0.4, 0.0],
          "minDistance": 1.6,
          "maxDistance": 1.9
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 3
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":56,
            "name":{
              "de":"Left shin",
              "en":"Left shin",
              "fr":"Left shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":62,
            "name":{
              "de":"Right shin",
              "en":"Right shin",
              "fr":"Right shin"
            },
            "transferSize": {
              "width": 5,
              "height": 10
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            }
          },
          {
            "id":74,
            "name":{
              "de":"Bib pocket",
              "en":"Bib pocket",
              "fr":"Bib pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":75,
            "name":{
              "de":"Above the bib pocket",
              "en":"Above the bib pocket",
              "fr":"Above the bib pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":77,
            "name":{
              "de":"Bib&brace brace connector",
              "en":"Bib&brace brace connector",
              "fr":"Bib&brace brace connector"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":126,
            "name":{
              "de":"On the left shin, parallel to the seam",
              "en":"On the left shin, parallel to the seam",
              "fr":"On the left shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          },
          {
            "id":127,
            "name":{
              "de":"On the right shin, parallel to the seam",
              "en":"On the right shin, parallel to the seam",
              "fr":"On the right shin, parallel to the seam"
            },
            "transferSize": {
              "width": 6,
              "height": 15
            }
          }
        ]
      },
      {
        "article": "26",
        "articleDescriptionUrl": {
          "de": "./descriptions/short_de.html",
          "en": "./descriptions/short_en.html",
          "fr": "./descriptions/short_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Short e.s.mymotion 2020, Damen CI",
          "en": "Shorts e.s.mymotion 2020, ladies' CI",
          "fr": "Short e.s.mymotion 2020, femmes CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 50.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 46.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 42.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "32", 
          "34",
          "36",
          "38",
          "40",
          "42",
          "44",
          "46"
        ],
        "tags": [
          "mymotion2020",
          "female",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.5, 2.0],
          "cameraStartPositionRight": [1.0, -0.5, 2.0],
          "cameraTargetPosition": [0.0, -0.5, 0.0]
        },
        "logoPlanes": [
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 22
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":54,
            "name":{
              "de":"Left above the knee",
              "en":"Left above the knee",
              "fr":"Left above the knee"
            },
            "stitchSize": {
              "width": 5,
              "height": 5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 22
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 5
            }
          }
        ]
      },
      {
        "article": "27",
        "articleDescriptionUrl": {
          "de": "./descriptions/short_de.html",
          "en": "./descriptions/short_en.html",
          "fr": "./descriptions/short_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "name": {
          "de": "Short e.s.mymotion 2020 CI",
          "en": "Shorts e.s.mymotion 2020 CI",
          "fr": "Short e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 59.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 54.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 50.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 46.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 42.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "38",
          "40",
          "42",
          "44",
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58",
          "60",
          "62",
          "64",
          "66"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.5, 2.0],
          "cameraStartPositionRight": [1.0, -0.5, 2.0],
          "cameraTargetPosition": [0.0, -0.5, 0.0]
        },
        "logoPlanes": [
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 3.5
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":50,
            "name":{
              "de":"Below the left trouser pocket",
              "en":"Below the left trouser pocket",
              "fr":"Below the left trouser pocket"
            },
            "stitchSize": {
              "width": 8,
              "height": 5
            },
            "transferSize": {
              "width": 8,
              "height": 5
            }
          },
          {
            "id":52,
            "name":{
              "de":"Left flap thigh pocket",
              "en":"Left flap thigh pocket",
              "fr":"Left flap thigh pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":54,
            "name":{
              "de":"Left above the knee",
              "en":"Left above the knee",
              "fr":"Left above the knee"
            },
            "stitchSize": {
              "width": 5,
              "height": 5
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 8
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 8
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 5
            }
          }
        ]
      },
      {
        "article": "28",
        "articleDescriptionUrl": {
          "de": "./descriptions/pirate_de.html",
          "en": "./descriptions/pirate_en.html",
          "fr": "./descriptions/pirate_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": false,
        "ventilationArticle": "29",
        "name": {
          "de": "Piratenhose e.s.mymotion 2020 CI",
          "en": "3/4 length trousers e.s.mymotion 2020 CI",
          "fr": "Corsaire e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 73.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 60.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 56.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.4, 2.0],
          "cameraStartPositionRight": [1.0, -0.4, 2.0],
          "cameraTargetPosition": [0.0, -0.4, 0.0]
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 3.5
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":50,
            "name":{
              "de":"Below the left trouser pocket",
              "en":"Below the left trouser pocket",
              "fr":"Below the left trouser pocket"
            },
            "stitchSize": {
              "width": 8,
              "height": 5
            },
            "transferSize": {
              "width": 8,
              "height": 5
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 5
            }
          }
        ]
      },
      {
        "article": "29",
        "articleDescriptionUrl": {
          "de": "./descriptions/pirate_vent_de.html",
          "en": "./descriptions/pirate_vent_en.html",
          "fr": "./descriptions/pirate_vent_fr.html"
        },
        "deliveryTime": {
          "de": "Lieferzeit ca. 6-8 Wochen",
          "en": "Deliver time about 6-8 weeks",
          "fr": "Deliver time about 6-8 weeks"
        },
        "hasVentilationZipper": true,
        "nonVentilationArticle": "28",
        "name": {
          "de": "Piratenhose e.s.mymotion 2020 CI",
          "en": "3/4 length trousers e.s.mymotion 2020 CI",
          "fr": "Corsaire e.s.mymotion 2020 CI"
        },
        "pricing": [
          {
            "minAmount": 0,
            "pricing": {
              "eur": 73.90,
              "chf": 0
            }
          },
          {
            "minAmount": 100,
            "pricing": {
              "eur": 69.90,
              "chf": 0
            }
          },
          {
            "minAmount": 250,
            "pricing": {
              "eur": 64.90,
              "chf": 0
            }
          },
          {
            "minAmount": 500,
            "pricing": {
              "eur": 60.90,
              "chf": 0
            }
          },
          {
            "minAmount": 1000,
            "pricing": {
              "eur": 56.90,
              "chf": 0
            }
          }
        ],
        "productImage": "",
        "sizes": [
          "46",
          "48",
          "50",
          "52",
          "54",
          "56",
          "58"
        ],
        "tags": [
          "mymotion2020",
          "male",
          "pants"
        ],
        "modelData": {
          "cameraStartPosition": [-3.0, -0.4, 2.0],
          "cameraStartPositionRight": [1.0, -0.4, 2.0],
          "cameraTargetPosition": [0.0, -0.4, 0.0]
        },
        "logoPlanes":[
          {
            "id":48,
            "name":{
              "de":"Left trouser pocket",
              "en":"Left trouser pocket",
              "fr":"Left trouser pocket"
            },
            "transferSize": {
              "width": 10,
              "height": 3.5
            }
          },
          {
            "id":49,
            "name":{
              "de":"Left thigh vertical",
              "en":"Left thigh vertical",
              "fr":"Left thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 5,
              "height": 25
            }
          },
          {
            "id":50,
            "name":{
              "de":"Below the left trouser pocket",
              "en":"Below the left trouser pocket",
              "fr":"Below the left trouser pocket"
            },
            "stitchSize": {
              "width": 8,
              "height": 5
            },
            "transferSize": {
              "width": 8,
              "height": 5
            }
          },
          {
            "id":55,
            "name":{
              "de":"Left knee pad pocket flap",
              "en":"Left knee pad pocket flap",
              "fr":"Left knee pad pocket flap"
            },
            "stitchSize": {
              "width": 10,
              "height": 2
            },
            "transferSize": {
              "width": 10,
              "height": 2
            }
          },
          {
            "id":58,
            "name":{
              "de":"Right thigh vertical",
              "en":"Right thigh vertical",
              "fr":"Right thigh vertical"
            },
            "stitchSize": {
              "width": 3,
              "height": 22
            },
            "transferSize": {
              "width": 10,
              "height": 25
            }
          },
          {
            "id":59,
            "name":{
              "de":"Below the right trouser pocket",
              "en":"Below the right trouser pocket",
              "fr":"Below the right trouser pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 8
            },
            "transferSize": {
              "width": 10,
              "height": 10
            }
          },
          {
            "id":60,
            "name":{
              "de":"Right above the knee",
              "en":"Right above the knee",
              "fr":"Right above the knee"
            },
            "stitchSize": {
              "width": 10,
              "height": 4.5
            },
            "transferSize": {
              "width": 10,
              "height": 5
            }
          },
          {
            "id":66,
            "name":{
              "de":"Left back pocket",
              "en":"Left back pocket",
              "fr":"Left back pocket"
            },
            "stitchSize": {
              "width": 10,
              "height": 5
            }
          }
        ]
      }    
    ],
    "inspirations": [
      {
        "article1": "20",
        "article2": "15",
        "baseColorId": "08",
        "contrastColorId": "14",
        "gender": Gender.Male,
        "straussLogo": StraussLogoType.Standard,
        "centerImage1": "./images/Inspo-a1.png",
        "centerImage2": "./images/Inspo-a2.png"
      },
      {
        "article1": "18",
        "article2": "14",
        "baseColorId": "10",
        "contrastColorId": "11",
        "gender": Gender.Female,
        "straussLogo": StraussLogoType.Standard,
        "centerImage1": "./images/Inspo-b1.png",
        "centerImage2": "./images/Inspo-b2.png"
      },
      {
        "article1": "20",
        "article2": "15",
        "baseColorId": "07",
        "contrastColorId": "15",
        "gender": Gender.Male,
        "straussLogo": StraussLogoType.Standard,
        "centerImage1": "./images/Inspo-c1.png",
        "centerImage2": "./images/Inspo-c2.png"
      },
      {
        "article1": "18",
        "article2": "14",
        "baseColorId": "06",
        "contrastColorId": "12",
        "gender": Gender.Female,
        "straussLogo": StraussLogoType.Standard,
        "centerImage1": "./images/Inspo-d1.png",
        "centerImage2": "./images/Inspo-d2.png"
      },
      {
        "article1": "20",
        "article2": "15",
        "baseColorId": "01",
        "contrastColorId": "17",
        "gender": Gender.Male,
        "straussLogo": StraussLogoType.Standard,
        "centerImage1": "./images/Inspo-e1.png",
        "centerImage2": "./images/Inspo-e2.png"
      }
    ],
    "logoAreas": [
        {
            "id": "transfer_1_50",
            "name": {
                "de": "Transferdruck - 4-Farbig",
                "en": "Transferdruck - 4-Farbig",
                "fr": "Transferdruck - 4-Farbig"
            },
            "minSize": 1,
            "maxSize": 50,
            "type": LogoType.Transfer,
            "pricing": []
        },
        {
            "id": "transfer_51_120",
            "name": {
                "de": "Transferdruck - 4-Farbig",
                "en": "Transferdruck - 4-Farbig",
                "fr": "Transferdruck - 4-Farbig"
            },
            "minSize": 51,
            "maxSize": 120,
            "type": LogoType.Transfer,
            "pricing": []
        },
        {
            "id": "transfer_121_600",
            "name": {
                "de": "Transferdruck - 4-Farbig",
                "en": "Transferdruck - 4-Farbig",
                "fr": "Transferdruck - 4-Farbig"
            },
            "minSize": 121,
            "maxSize": 600,
            "type": LogoType.Transfer,
            "pricing": []
        },
        {
            "id": "transfer_601_900",
            "name": {
                "de": "Transferdruck - 4-Farbig",
                "en": "Transferdruck - 4-Farbig",
                "fr": "Transferdruck - 4-Farbig"
            },
            "minSize": 601,
            "maxSize": 900,
            "type": LogoType.Transfer,
            "pricing": []
        },
        {
            "id": "direct_1_45",
            "name": {
                "de": "Direkteinstickung - Firmenlogo",
                "en": "Direkteinstickung - Firmenlogo",
                "fr": "Direkteinstickung - Firmenlogo"
            },
            "minSize": 1,
            "maxSize": 45,
            "type": LogoType.Stitched,
            "pricing": []
        },
        {
            "id": "direct_46_80",
            "name": {
                "de": "Direkteinstickung - Firmenlogo",
                "en": "Direkteinstickung - Firmenlogo",
                "fr": "Direkteinstickung - Firmenlogo"
            },
            "minSize": 46,
            "maxSize": 80,
            "type": LogoType.Stitched,
            "pricing": []
        },
        {
            "id": "direct_81_200",
            "name": {
                "de": "Direkteinstickung - Firmenlogo",
                "en": "Direkteinstickung - Firmenlogo",
                "fr": "Direkteinstickung - Firmenlogo"
            },
            "minSize": 81,
            "maxSize": 200,
            "type": LogoType.Stitched,
            "pricing": []
        },
        {
            "id": "direct_201_260",
            "name": {
                "de": "Direkteinstickung - Firmenlogo",
                "en": "Direkteinstickung - Firmenlogo",
                "fr": "Direkteinstickung - Firmenlogo"
            },
            "minSize": 201,
            "maxSize": 260,
            "type": LogoType.Stitched,
            "pricing": []
        },
        {
            "id": "direct_261_390",
            "name": {
                "de": "Direkteinstickung - Firmenlogo",
                "en": "Direkteinstickung - Firmenlogo",
                "fr": "Direkteinstickung - Firmenlogo"
            },
            "minSize": 261,
            "maxSize": 390,
            "type": LogoType.Stitched,
            "pricing": []
        }
    ],
    "minimumOrderAmount": {
        "defaultAmount": 30,
        "products": [
            {
                "articles": ["16", "17"],
                "amount": 30
            },
            {
                "articles": ["18", "19", "20", "21"],
                "amount": 50
            },
            {
                "articles": ["22", "23", "24", "25"],
                "amount": 50
            },
            {
                "articles": ["26", "27"],
                "amount": 50
            },
            {
                "articles": ["28", "29"],
                "amount": 50
            }
        ]
    }
 };

export default function library(state: LibraryData = initialState, action : any) : LibraryData {
    switch (action.type) {
        case LIBRARY_GET_RECEIVE:
            return Object.assign({}, state, action.library)
        default:
            return state;
    }
}
  